import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { PageIcon } from "@/components/PageIcon";
import { Plus, X, ChevronDown, ChevronRight, Loader2, Send, Archive, RotateCcw, Trash2 } from "lucide-react";
import S3Uploader from "@/components/S3Uploader";
import { AssignStaffDialog } from "@/components/AssignStaffDialog";
import { UserCheck } from "lucide-react";

const TYPES = ["Bug", "Idea", "Request"];
const STATUSES = ["Open", "In Progress", "Resolved", "Closed"];
const PRIORITIES = ["Low", "Normal", "High", "Urgent"];
const TYPE_COLOUR = { Bug: "bg-rose-100 text-rose-700", Idea: "bg-violet-100 text-violet-700", Request: "bg-sky-100 text-sky-700" };
const STATUS_COLOUR = { Open: "bg-amber-100 text-amber-700", "In Progress": "bg-indigo-100 text-indigo-700", Resolved: "bg-emerald-100 text-emerald-700", Closed: "bg-slate-200 text-slate-600" };
const selCls = "appearance-none rounded-lg border border-slate-200 bg-slate-50 px-2 py-1 pr-7 text-xs text-slate-900 focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100 focus:outline-none";
const fmt = (d) => (d ? new Date(d).toLocaleDateString("en-GB", { day: "2-digit", month: "short" }) : "");

export default function TicketsPage() {
  const [items, setItems] = useState([]);
  const [canManage, setCanManage] = useState(false);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("All");
  const [creating, setCreating] = useState(false);
  const [expanded, setExpanded] = useState(null);
  const [showArchived, setShowArchived] = useState(false);

  const load = () => api.get(`/tickets?include_archived=${showArchived}`).then((r) => { setItems(r.data.items || []); setCanManage(r.data.can_manage); }).catch(() => {}).finally(() => setLoading(false));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [showArchived]);
  useEffect(() => {
    const t = new URLSearchParams(window.location.search).get("ticket");
    if (t && /^\d+$/.test(t)) setExpanded(Number(t));
  }, []);

  const archivedCount = items.filter((t) => t.archived).length;
  const shown = filter === "All" ? items : items.filter((t) => t.status === filter);

  return (
    <div className="px-6 lg:px-8 pt-3 lg:pt-4 pb-6 lg:pb-8" data-testid="tickets-page">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3"><PageIcon /><div>
          <h1 className="text-2xl font-bold text-slate-900">Support & Ideas</h1>
          <p className="text-sm text-slate-500">Raise a bug, share an idea or send a request. The dev team picks these up here.</p>
        </div></div>
        <div className="flex items-center gap-2">
          {canManage && (
            <button
              onClick={() => setShowArchived((v) => !v)}
              data-testid="tickets-toggle-archived"
              className={`inline-flex items-center gap-1.5 rounded-lg text-sm font-semibold px-3 py-2 border transition-colors ${showArchived ? "bg-slate-800 text-white border-slate-800" : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"}`}
            >
              <Archive className="w-4 h-4" /> {showArchived ? "Showing archived" : "Archived"}
              {showArchived && archivedCount > 0 && <span className="ml-0.5 rounded-full bg-white/20 px-1.5 text-[11px]">{archivedCount}</span>}
            </button>
          )}
          <Button className="gap-1.5 bg-indigo-600 hover:bg-indigo-700 text-white" onClick={() => setCreating(true)} data-testid="ticket-add-btn"><Plus className="w-4 h-4" /> New ticket</Button>
        </div>
      </div>

      <div className="flex flex-wrap gap-2 mb-4">
        {["All", ...STATUSES].map((s) => (
          <button key={s} onClick={() => setFilter(s)} data-testid={`ticket-filter-${s.replace(/\s/g, "-")}`}
            className={`text-xs font-medium rounded-full px-3 py-1.5 border transition-colors ${filter === s ? "bg-indigo-600 text-white border-indigo-600" : "bg-white text-slate-600 border-slate-200 hover:border-indigo-300"}`}>
            {s}{s !== "All" ? ` (${items.filter((t) => t.status === s).length})` : ` (${items.length})`}
          </button>
        ))}
      </div>

      <div className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden">
        {loading ? <div className="p-8 text-center text-slate-400"><Loader2 className="w-5 h-5 animate-spin inline" /></div>
          : shown.length === 0 ? <div className="p-8 text-center text-sm text-slate-400">No tickets{filter !== "All" ? ` in ${filter}` : ""}.</div>
          : shown.map((t) => (
            <TicketRow key={t.id} t={t} canManage={canManage} expanded={expanded === t.ticket_no}
              onToggle={() => setExpanded(expanded === t.ticket_no ? null : t.ticket_no)} onChange={load} />
          ))}
      </div>

      {creating && <TicketForm onClose={() => setCreating(false)} onSaved={() => { setCreating(false); load(); }} />}
    </div>
  );
}

function TicketRow({ t, canManage, expanded, onToggle, onChange }) {
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(false);
  const [confirmDel, setConfirmDel] = useState(false);
  const setField = async (k, v) => { try { await api.patch(`/tickets/${t.ticket_no}`, { [k]: v }); onChange(); } catch (e) { toast.error(e.response?.data?.detail || "Failed"); } };
  const act = async (fn, msg) => {
    setBusy(true);
    try { await fn(); toast.success(msg); onChange(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  const archive = () => act(() => api.post(`/tickets/${t.ticket_no}/archive`), `Ticket #${t.ticket_no} archived`);
  const unarchive = () => act(() => api.post(`/tickets/${t.ticket_no}/unarchive`), `Ticket #${t.ticket_no} restored`);
  const del = () => act(() => api.delete(`/tickets/${t.ticket_no}`), `Ticket #${t.ticket_no} deleted`);
  const addComment = async () => {
    if (!comment.trim()) return;
    setBusy(true);
    try { await api.post(`/tickets/${t.ticket_no}/comments`, { body: comment }); setComment(""); onChange(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  return (
    <div className={`border-b border-slate-100 last:border-0 ${t.archived ? "opacity-70" : ""}`} data-testid={`ticket-${t.ticket_no}`}>
      <button onClick={onToggle} className="w-full flex items-center gap-3 px-4 py-3 hover:bg-slate-50 text-left">
        {expanded ? <ChevronDown className="w-4 h-4 text-slate-400 shrink-0" /> : <ChevronRight className="w-4 h-4 text-slate-400 shrink-0" />}
        <span className="font-mono text-xs text-slate-400 shrink-0">#{t.ticket_no}</span>
        <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full shrink-0 ${TYPE_COLOUR[t.type] || "bg-slate-100"}`}>{t.type}</span>
        <span className="flex-1 min-w-0 truncate text-sm font-medium text-slate-800">{t.title}</span>
        {t.archived && <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full shrink-0 bg-slate-200 text-slate-500 uppercase tracking-wide">Archived</span>}
        <span className="hidden sm:block text-xs text-slate-400 shrink-0">{t.author_name}</span>
        <span className="hidden md:block text-xs text-slate-500 shrink-0" data-testid={`ticket-owner-badge-${t.ticket_no}`}>{t.owner_name ? `Owner: ${t.owner_name}` : "Unassigned"}</span>
        <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full shrink-0 ${STATUS_COLOUR[t.status]}`}>{t.status}</span>
      </button>
      {expanded && (
        <div className="px-5 pb-4 pt-1 bg-slate-50/50">
          {t.description && <p className="text-sm text-slate-600 whitespace-pre-wrap mb-3">{t.description}</p>}
          <TicketOwner t={t} canManage={canManage} onChange={onChange} />
          {canManage && (
            <div className="flex flex-wrap items-center gap-3 mb-3">
              {[["status", STATUSES], ["priority", PRIORITIES], ["type", TYPES]].map(([k, opts]) => (
                <label key={k} className="flex items-center gap-1.5 text-xs text-slate-500 capitalize">{k}
                  <div className="relative">
                    <select value={t[k]} onChange={(e) => setField(k, e.target.value)} className={selCls} data-testid={`ticket-${k}-${t.ticket_no}`}>
                      {opts.map((o) => <option key={o} value={o}>{o}</option>)}
                    </select>
                    <ChevronDown className="pointer-events-none absolute right-1.5 top-1/2 -translate-y-1/2 w-3 h-3 text-slate-400" />
                  </div>
                </label>
              ))}
              <div className="flex-1" />
              {t.archived ? (
                <button onClick={unarchive} disabled={busy} data-testid={`ticket-restore-${t.ticket_no}`}
                  className="inline-flex items-center gap-1 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-600 text-xs font-semibold px-2.5 py-1.5 transition-colors disabled:opacity-50">
                  <RotateCcw className="w-3.5 h-3.5" /> Restore
                </button>
              ) : (
                <button onClick={archive} disabled={busy} data-testid={`ticket-archive-${t.ticket_no}`}
                  className="inline-flex items-center gap-1 rounded-lg bg-amber-50 hover:bg-amber-100 text-amber-700 text-xs font-semibold px-2.5 py-1.5 transition-colors disabled:opacity-50">
                  <Archive className="w-3.5 h-3.5" /> Archive
                </button>
              )}
              {confirmDel ? (
                <span className="inline-flex items-center gap-1">
                  <button onClick={del} disabled={busy} data-testid={`ticket-delete-confirm-${t.ticket_no}`}
                    className="inline-flex items-center gap-1 rounded-lg bg-rose-600 hover:bg-rose-500 text-white text-xs font-semibold px-2.5 py-1.5 transition-colors disabled:opacity-50">
                    {busy && <Loader2 className="w-3.5 h-3.5 animate-spin" />} Confirm delete
                  </button>
                  <button onClick={() => setConfirmDel(false)} className="text-xs font-semibold text-slate-500 px-2 py-1.5 hover:text-slate-700">Cancel</button>
                </span>
              ) : (
                <button onClick={() => setConfirmDel(true)} data-testid={`ticket-delete-${t.ticket_no}`}
                  className="inline-flex items-center gap-1 rounded-lg bg-rose-50 hover:bg-rose-100 text-rose-700 text-xs font-semibold px-2.5 py-1.5 transition-colors">
                  <Trash2 className="w-3.5 h-3.5" /> Delete
                </button>
              )}
            </div>
          )}
          <div className="space-y-2">
            {(t.comments || []).map((c) => (
              <div key={c.id} className="rounded-lg bg-white border border-slate-200 px-3 py-2">
                <div className="text-sm text-slate-700 whitespace-pre-wrap">{c.body}</div>
                <div className="text-[10px] text-slate-400 mt-1">{c.author_name} · {fmt(c.ts)}</div>
              </div>
            ))}
          </div>
          <div className="flex items-center gap-2 mt-2">
            <Input value={comment} onChange={(e) => setComment(e.target.value)} placeholder="Add a comment…" className="h-9" data-testid={`ticket-comment-input-${t.ticket_no}`} onKeyDown={(e) => e.key === "Enter" && addComment()} />
            <Button size="sm" className="gap-1.5 bg-indigo-600 hover:bg-indigo-700 text-white" disabled={busy} onClick={addComment} data-testid={`ticket-comment-send-${t.ticket_no}`}><Send className="w-3.5 h-3.5" /></Button>
          </div>
          <div className="mt-3 pt-3 border-t border-slate-100">
            <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-400 mb-2">Attachments</div>
            <S3Uploader recordType="tickets" recordId={String(t.ticket_no)} category="Ticket" canManage accept="image/*,video/*,application/pdf" serverSide />
          </div>
        </div>
      )}
    </div>
  );
}

function TicketOwner({ t, canManage, onChange }) {
  const [open, setOpen] = useState(false);
  const hist = [...(t.owner_history || [])].reverse();
  return (
    <div className="mb-3 rounded-lg border border-slate-200 bg-white px-3 py-2" data-testid={`ticket-owner-${t.ticket_no}`}>
      <div className="flex flex-wrap items-center gap-2 text-sm">
        <UserCheck className="w-4 h-4 text-indigo-500" />
        <span className="text-slate-700" data-testid={`ticket-owner-name-${t.ticket_no}`}>{t.owner_name ? <>Owner: <b>{t.owner_name}</b></> : "No owner yet"}</span>
        {canManage && (
          <button onClick={() => setOpen(true)} className="text-xs font-semibold text-indigo-600 hover:underline" data-testid={`ticket-owner-assign-${t.ticket_no}`}>
            {t.owner_name ? "Change owner" : "Assign owner"}
          </button>
        )}
      </div>
      {hist.length > 0 && (
        <ul className="mt-2 space-y-0.5 text-xs text-slate-500" data-testid={`ticket-owner-history-${t.ticket_no}`}>
          {hist.map((h, i) => (
            <li key={i}>{new Date(h.at).toLocaleString("en-GB", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })} · {h.by}: {h.from || "Unassigned"} → {h.to || "Unassigned"}</li>
          ))}
        </ul>
      )}
      <AssignStaffDialog open={open} onClose={() => setOpen(false)} perm="tickets.manage" testid={`ticket-owner-dialog`}
        title={`Owner for ticket #${t.ticket_no}`} current={t.owner_id ? [t.owner_id] : []}
        endpoint={`/tickets/${t.ticket_no}/owner`} bodyFor={(ids) => ({ user_id: ids[0] || null })} onSaved={onChange} />
    </div>
  );
}

function TicketForm({ onClose, onSaved }) {
  const [f, setF] = useState({ type: "Bug", title: "", description: "", priority: "Normal" });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const save = async () => {
    if (!f.title.trim()) { toast.error("Title is required"); return; }
    setBusy(true);
    try { await api.post("/tickets", f); toast.success("Ticket raised"); onSaved(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not raise ticket"); }
    finally { setBusy(false); }
  };
  const sel = "w-full appearance-none rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 pr-8 text-sm focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100 focus:outline-none";
  return (
    <div className="fixed inset-0 z-50 bg-slate-900/40 flex justify-end" onClick={onClose} data-testid="ticket-form">
      <div className="bg-white h-full w-full sm:w-1/2 sm:max-w-none shadow-2xl overflow-y-auto animate-in slide-in-from-right duration-300" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
          <h3 className="text-xl font-bold text-slate-900">New Ticket</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div><label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Type</label>
              <div className="relative mt-1"><select value={f.type} onChange={(e) => set("type", e.target.value)} className={sel} data-testid="ticket-type"><option>Bug</option><option>Idea</option><option>Request</option></select><ChevronDown className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" /></div></div>
            <div><label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Priority</label>
              <div className="relative mt-1"><select value={f.priority} onChange={(e) => set("priority", e.target.value)} className={sel} data-testid="ticket-priority">{PRIORITIES.map((p) => <option key={p}>{p}</option>)}</select><ChevronDown className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" /></div></div>
          </div>
          <div><label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Title</label>
            <Input value={f.title} onChange={(e) => set("title", e.target.value)} className="mt-1" data-testid="ticket-title" placeholder="Short summary" /></div>
          <div><label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Details</label>
            <textarea value={f.description} onChange={(e) => set("description", e.target.value)} rows={5} className="mt-1 w-full rounded-lg border border-slate-200 bg-slate-50 p-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-100" data-testid="ticket-description" placeholder="What happened, or what would you like?" /></div>
        </div>
        <div className="px-6 py-4 border-t border-slate-100 flex justify-end gap-2">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button className="bg-indigo-600 hover:bg-indigo-700 text-white" disabled={busy} onClick={save} data-testid="ticket-save">{busy && <Loader2 className="w-4 h-4 animate-spin mr-1" />}Raise ticket</Button>
        </div>
      </div>
    </div>
  );
}
